import React, { ReactNode, useState } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import {
  BUTTON_THEMES,
  BUTTON_TYPES,
  CARD_TYPES,
} from '../../../shared/constants';
import { IContentfulLinkWithModal, IImage } from '../../../types/shared';

import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import { DEQ_TYPES } from '../../../utils/deq';
import { getCtaLink, ReactMarkdownExtended } from '../../../utils/generic';

import mq from '../../../styles/breakpoints';
import buttonStyles, { CtaWrapper } from '../../../styles/buttonStyles';
import Colors from '../../../styles/colors';
import { H2, IntroText } from '../../../styles/generic';
import listStyles from '../../../styles/listStyles';
import Spacings from '../../../styles/Spacings';

import Modal from '../../organisms/modal/Modal';
import { SimpleContent } from '../simple-content/SimpleContent';

interface Props {
  title: string;
  image?: IImage;
  productName?: string;
  primaryLink?: IContentfulLinkWithModal;
  secondaryLink?: IContentfulLinkWithModal;
  children?: ReactNode;
}

const HighlightCard = ({
  image,
  primaryLink,
  secondaryLink,
  title,
  productName,
  children,
}: Props) => {
  const [showModal, setShowModalHandler] = useState(false);
  const [activeCtaLink, setActiveCtaLink] =
    useState<IContentfulLinkWithModal>();

  const ctaPrimaryLink =
    primaryLink && !primaryLink.dialogueModal
      ? getCtaLink(primaryLink)
      : undefined;

  const ctaSecondaryLink =
    secondaryLink && !secondaryLink.dialogueModal
      ? getCtaLink(secondaryLink)
      : undefined;

  const handleOpenModal = (link: IContentfulLinkWithModal) => {
    setShowModalHandler(true);
    setActiveCtaLink(link);
  };

  return (
    <Section id={CARD_TYPES.highlight}>
      {image && (
        <ImageContainer data-testid="image">
          <GatsbyImage alt={title} image={image.gatsbyImageData} />
        </ImageContainer>
      )}
      <TextContainer>
        <StyledTitle data-testid="title">{title}</StyledTitle>
        <Description data-testid="description" as="div">
          {children}
        </Description>
        {(primaryLink || secondaryLink) && (
          <CtaWrapper>
            {primaryLink && (
              <StyledPrimaryButton
                showModal={!!primaryLink?.dialogueModal}
                onButtonClick={() =>
                  primaryLink.dialogueModal && handleOpenModal(primaryLink)
                }
                type={primaryLink?.type}
                label={primaryLink.label || primaryLink.name}
                buttonTheme={primaryLink.theme || BUTTON_THEMES.highlight}
                to={ctaPrimaryLink as string}
                iconPosition={primaryLink?.iconPosition}
                icon={primaryLink?.icon}
                deqComponentTitle={title}
              />
            )}
            {secondaryLink && (
              <StyledSecondaryButton
                showModal={!!secondaryLink?.dialogueModal}
                onButtonClick={() =>
                  secondaryLink.dialogueModal && handleOpenModal(secondaryLink)
                }
                type={secondaryLink?.type}
                label={secondaryLink.label || secondaryLink.name}
                buttonTheme={secondaryLink.theme || BUTTON_THEMES.default}
                to={ctaSecondaryLink as string}
                iconPosition={secondaryLink?.iconPosition}
                icon={secondaryLink?.icon}
                deqComponentTitle={title}
                deqCustomAttributes={{
                  type: DEQ_TYPES.textLink,
                  elementType: DEQ_TYPES.textLink,
                  elementStyle: BUTTON_TYPES.secondary,
                }}
              />
            )}
          </CtaWrapper>
        )}
      </TextContainer>
      <Modal
        show={showModal}
        onRequestCloseHandler={() => setShowModalHandler(!showModal)}
      >
        {activeCtaLink?.dialogueModal && (
          <SimpleContent
            setShowModal={(event) => {
              setShowModalHandler(event);
            }}
            title={activeCtaLink.dialogueModal.title}
            buttonLabel="Close"
            primaryLink={activeCtaLink?.dialogueModal.primaryLink}
          >
            <ReactMarkdownExtended componentType={CARD_TYPES.highlight}>
              {
                activeCtaLink.dialogueModal.body.childMarkdownRemark
                  .rawMarkdownBody
              }
            </ReactMarkdownExtended>
          </SimpleContent>
        )}
      </Modal>
    </Section>
  );
};

export default HighlightCard;

const Section = styled.section`
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;

  ${mq.from.breakpoint.M`
    flex-direction: row;
  `}

  ${mq.from.breakpoint.L`
    align-items: center;
  `}
`;

const ImageContainer = styled.div`
  flex-shrink: 0;
  width: 100%;

  div > div:first-child {
    padding-top: 63% !important;
  }

  ${mq.from.breakpoint.M`
    width: 348px;

    div > div:first-child {
      padding-top: 100% !important;
    }
  `}

  ${mq.from.breakpoint.L`
    width: 50%;

    div > div:first-child {
      padding-top: 66% !important;
    }
  `}
`;

const TextContainer = styled.div`
  background-color: ${Colors.white};
  box-shadow: ${({ theme }) => theme.elevations.abnElevationWell};
  box-sizing: border-box;
  margin: -${Spacings.S} ${Spacings.S} 0;
  padding: ${Spacings.S};
  position: relative;
  width: calc(100% - ${Spacings.L});

  ${mq.from.breakpoint.M`
    padding: ${Spacings.M};
    margin: 0 0 0 -${Spacings.M};
    width: 100%;
  `}

  ${mq.from.breakpoint.L`
    padding: ${Spacings.XL};
    margin: 0 0 0 -${Spacings.XL};
  `}
`;

const StyledTitle = styled(H2)`
  margin: 0 0 ${Spacings.XS};
`;

const Description = styled(IntroText)`
  ${listStyles.unorderedList}

  > p {
    margin: 0;
  }
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  ${buttonStyles.primaryButtonPosition}
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  ${buttonStyles.secondaryButtonPosition}
`;
